<template>
  <div class="dashboard-month-grid">
    <article
      v-for="card in cardItems"
      :key="card.label"
      :class="[
        'dashboard-month-card',
        card.desktopClass,
      ]"
    >
      <div class="dashboard-month-card__icon text-primary">
        <i :class="card.icon"></i>
      </div>

      <div class="dashboard-month-card__body">
        <span class="dashboard-month-card__label">{{ card.label }}</span>
        <h4 class="dashboard-month-card__value">{{ card.value }}</h4>
        <span
          v-for="meta in card.meta"
          :key="meta"
          class="dashboard-month-card__meta"
        >
          {{ meta }}
        </span>
        <button v-if="card.failed" type="button" class="dashboard-month-card__retry" :disabled="insightsLoading" @click="retryInsights">{{ insightsLoading ? 'Carregando...' : 'Tentar novamente' }}</button>
      </div>
      <div v-if="card.segments && card.segments.length" class="dashboard-month-card__breakdown">
        <div class="dashboard-month-card__distribution" role="img" :aria-label="card.distributionLabel">
          <span v-for="segment in card.segments" :key="segment.label" :class="`dashboard-month-card__segment--${segment.color}`" :style="{ width: `${segment.width}%` }"></span>
        </div>
        <ul class="dashboard-month-card__legend">
          <li v-for="segment in card.segments" :key="segment.label">
            <span class="dashboard-month-card__dot" :class="`dashboard-month-card__segment--${segment.color}`" aria-hidden="true"></span>
            <span>{{ segment.label }}: <strong>{{ segment.count }}</strong> ({{ formatPercent(segment.percentage) }})</span>
          </li>
        </ul>
      </div>
    </article>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useStore } from "vuex";

type Segment = { label: string; color: string; count: number; percentage: number; width: number };
type DashboardCard = {
  label: string; value: string | number; meta: string[]; icon: string; desktopClass: string;
  segments?: Segment[]; distributionLabel?: string; failed?: boolean;
};

export default defineComponent({
  name: "CardsDashboardMonth",
  setup() {
    const store = useStore();

    const formatPercent = (value: number) => `${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
    const insightsLoading = computed(() => store.getters.getDashboardInsightsLoading);
    const retryInsights = () => store.dispatch('fetchDashboardInsights', store.getters.getDashboardInsightsQuery);

    const getCardsDashboardMonth = computed(
      () => store.getters.getCardsDashboardMonth || {}
    );

    const cardItems = computed<DashboardCard[]>(() => {
      const dashboardMonth = getCardsDashboardMonth.value;
      const attendant = dashboardMonth.attendant_of_the_month || {};
      const insights = store.getters.getDashboardInsights;
      const failed = Boolean(store.getters.getDashboardInsightsError);
      const loading = insightsLoading.value && !insights && !failed;
      const automation = insights?.automation;
      const satisfaction = insights?.satisfaction;
      const segment = (label: string, color: string, count: number, percentage: number, total: number): Segment => ({ label, color, count, percentage, width: total ? count * 100 / total : 0 });
      const automationSegments = automation?.total ? [
        segment('IA', 'primary', automation.automated, automation.automated_percentage, automation.total),
        segment('Manual', 'neutral', automation.manual, automation.manual_percentage, automation.total),
      ] : [];
      const satisfactionSegments = satisfaction?.rated ? [
        segment('Bom', 'good', satisfaction.good, satisfaction.good_percentage, satisfaction.rated),
        segment('Regular', 'regular', satisfaction.regular, satisfaction.regular_percentage, satisfaction.rated),
        segment('Ruim', 'poor', satisfaction.poor, satisfaction.poor_percentage, satisfaction.rated),
      ] : [];
      const statusMeta = loading ? ['Carregando indicadores...'] : failed ? ['Não foi possível carregar este indicador.'] : [];

      return [
        {
          label: "Agendamentos",
          value: dashboardMonth.scheduled ?? 0,
          meta: ["Mês inteiro"],
          icon: "ph ph-calendar-check",
          desktopClass: "dashboard-month-card--desktop-half",
        },
        {
          label: "Cancelados",
          value: dashboardMonth.canceled ?? 0,
          meta: ["Acompanhamento mensal"],
          icon: "ph ph-calendar-x",
          desktopClass: "dashboard-month-card--desktop-half",
        },
        {
          label: "Novos clientes",
          value: dashboardMonth.new_clients ?? 0,
          meta: ["Entradas confirmadas no mês", "Dados gerais da empresa"],
          icon: "ph ph-user-plus",
          desktopClass: "dashboard-month-card--desktop-half",
        },
        {
          label: "Atendente do mês",
          value: attendant.name || "-",
          meta: [
            `Qtd. serviços: ${attendant.qtd_services ?? 0}`,
            `Valor total: R$ ${attendant.amount_received}`,
          ],
          icon: "ph ph-identification-badge",
          desktopClass: "dashboard-month-card--desktop-half",
        },
        {
          label: 'Agendamentos pela IA',
          value: loading || failed ? '—' : automation?.automated ?? '—',
          meta: statusMeta.length ? statusMeta : automation?.total ? [] : ['Nenhum agendamento no mês.'],
          icon: 'ph ph-robot',
          desktopClass: 'dashboard-month-card--insight',
          segments: automationSegments,
          distributionLabel: automation ? `IA: ${formatPercent(automation.automated_percentage)}. Manual: ${formatPercent(automation.manual_percentage)}.` : '',
          failed,
        },
        {
          label: 'Satisfação dos clientes',
          value: loading || failed ? '—' : satisfaction?.rated ? formatPercent(satisfaction.good_percentage) : 'Sem avaliações',
          meta: [...(statusMeta.length ? statusMeta : satisfaction?.rated ? [
            `${satisfaction.rated} avaliações no mês${satisfaction.unrated ? ` · ${satisfaction.unrated} sem nota válida` : ''}`,
          ] : satisfaction?.unrated ? [`${satisfaction.unrated} registros sem nota válida.`] : ['Nenhuma avaliação no mês.']), 'Dados gerais da empresa'],
          icon: 'ph ph-star',
          desktopClass: 'dashboard-month-card--insight',
          segments: satisfactionSegments,
          distributionLabel: satisfaction ? `Bom: ${formatPercent(satisfaction.good_percentage)}. Regular: ${formatPercent(satisfaction.regular_percentage)}. Ruim: ${formatPercent(satisfaction.poor_percentage)}.` : '',
          failed,
        },
      ];
    });

    return {
      cardItems,
      formatPercent,
      insightsLoading,
      retryInsights,
    };
  },
});
</script>

<style scoped>
.dashboard-month-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.dashboard-month-card {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  min-height: 164px;
  padding: 18px;
  border: 1px solid #eef0f7;
  border-radius: 7px;
  background: #fff;
}

.dashboard-month-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  min-width: 48px;
  height: 48px;
  border-radius: 7px;
  background: rgba(101, 96, 240, 0.1);
  font-size: 22px;
}

.dashboard-month-card__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.dashboard-month-card__label {
  overflow-wrap: anywhere;
  margin-bottom: 8px;
  color: #8e8da1;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.dashboard-month-card__value {
  margin: 0 0 8px;
  color: #2b2a3f;
  font-size: 26px;
  line-height: 1.05;
  word-break: break-word;
  font-size: 16pt;
  font-weight: 900;
}

.dashboard-month-card__meta {
  color: #5b5f7b;
  font-size: 13px;
  line-height: 1.4;
}

.dashboard-month-card--insight { overflow-wrap: anywhere; }
.dashboard-month-card__breakdown { width: 100%; }
.dashboard-month-card__distribution { display: flex; height: 7px; margin: 0 0 9px; background: #edf0f5; border-radius: 5px; overflow: hidden; }
.dashboard-month-card__legend { display: flex; flex-wrap: wrap; gap: 5px 12px; padding: 0; margin: 0; list-style: none; font-size: 11px; color: #5b5f7b; }
.dashboard-month-card__legend li { display: flex; align-items: center; gap: 5px; min-width: 0; }
.dashboard-month-card__dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.dashboard-month-card__segment--primary { background: #6560f0; }
.dashboard-month-card__segment--neutral { background: #b2b8c9; }
.dashboard-month-card__segment--good { background: #08795c; }
.dashboard-month-card__segment--regular { background: #e8a12a; }
.dashboard-month-card__segment--poor { background: #d94e62; }
.dashboard-month-card__retry { align-self: flex-start; border: 0; border-radius: 5px; background: transparent; color: #6560f0; padding: 6px 0; min-height: 44px; font-size: 13px; }
.dashboard-month-card__retry:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }

@media (min-width: 1400px) {
  .dashboard-month-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .dashboard-month-grid {
    gap: 10px;
    margin-bottom: 20px;
  }

  .dashboard-month-card {
    min-height: 0;
    padding: 14px 12px;
    gap: 10px;
    border-radius: 7px;
  }

  .dashboard-month-card__icon {
    width: 40px;
    min-width: 40px;
    height: 40px;
    border-radius: 7px;
    font-size: 18px;
  }

  .dashboard-month-card__label {
    margin-bottom: 6px;
    font-size: 10px;
  }

  .dashboard-month-card__value {
    margin-bottom: 6px;
    font-size: 18px;
  }

  .dashboard-month-card__meta {
    font-size: 11px;
    line-height: 1.3;
  }
}
@media (max-width: 375px) {
  .dashboard-month-card { flex-direction: column; gap: 8px; }
}
</style>

<style>
body.dark .dashboard-month-card {
  background: #2b2a3f;
  border-color: #45445f;
}

body.dark .dashboard-month-card__icon {
  background: rgba(101, 96, 240, 0.18);
}

body.dark .dashboard-month-card__label,
body.dark .dashboard-month-card__meta,
body.dark .dashboard-month-card__legend {
  color: #b8b6cf;
}

body.dark .dashboard-month-card__value {
  color: #ffffff;
}
</style>
